import axios from 'axios';
import ApiGet from '../sections/mainheader/apiget';

const RENEWAL_WINDOW_MS = 5 * 60 * 1000;
const RETRY_DELAY_MS = 30 * 1000;
const RENEWAL_REQUEST_TIMEOUT_MS = 30 * 1000;

let renewalTimer;
let retryTimer;
let expiryTimer;
let renewalInProgress = null;
let currentToken = '';
let handlers = {};

const getExpiryTime = (token) => {
    try {
        const payload = token.split('.')[1];
        const decoded = JSON.parse(window.atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
        return decoded.exp * 1000;
    } catch {
        return null;
    }
};

const clearTimers = () => {
    clearTimeout(renewalTimer);
    clearTimeout(retryTimer);
    clearTimeout(expiryTimer);
    renewalTimer = null;
    retryTimer = null;
    expiryTimer = null;
};

const stopForInvalidToken = () => {
    clearTimers();
    currentToken = '';
    handlers.onUnauthorized?.();
};

const scheduleRetry = () => {
    const expiryTime = getExpiryTime(currentToken);
    if (!expiryTime || expiryTime <= Date.now()) {
        stopForInvalidToken();
        return;
    }

    clearTimeout(retryTimer);
    retryTimer = setTimeout(renewToken, Math.min(RETRY_DELAY_MS, expiryTime - Date.now()));
};

const scheduleRenewal = () => {
    clearTimeout(renewalTimer);

    const expiryTime = getExpiryTime(currentToken);
    if (!expiryTime) {
        return;
    }

    const delay = expiryTime - Date.now() - RENEWAL_WINDOW_MS;
    renewalTimer = setTimeout(renewToken, Math.max(0, delay));
};

const scheduleExpiryLogout = () => {
    clearTimeout(expiryTimer);

    const expiryTime = getExpiryTime(currentToken);
    if (!expiryTime) {
        return;
    }

    expiryTimer = setTimeout(stopForInvalidToken, Math.max(0, expiryTime - Date.now()));
};

const renewToken = async () => {
    if (renewalInProgress) {
        return renewalInProgress;
    }

    const tokenBeingRenewed = currentToken;
    const expiryTime = getExpiryTime(tokenBeingRenewed);
    if (!expiryTime || expiryTime <= Date.now()) {
        stopForInvalidToken();
        return null;
    }

    renewalInProgress = ApiGet()
        .then((apiUrl) => axios.post(`${apiUrl}Login/RenewToken`, null, {
            headers: { authheader: tokenBeingRenewed },
            timeout: RENEWAL_REQUEST_TIMEOUT_MS,
        }))
        .then((response) => {
            const renewedToken = response.data?.accessToken || response.data?.AccessToken;
            if (!renewedToken) {
                throw new Error('RenewToken did not return an access token.');
            }

            // A response at the renewal-window boundary can legitimately return
            // the current token. Wait briefly instead of immediately retrying it.
            if (renewedToken === tokenBeingRenewed) {
                scheduleRetry();
                return renewedToken;
            }

            currentToken = renewedToken;
            handlers.onRenewed?.(renewedToken);
            scheduleRenewal();
            return renewedToken;
        })
        .catch((error) => {
            if (error?.response?.status === 401) {
                stopForInvalidToken();
                return null;
            }

            scheduleRetry();
            return null;
        })
        .finally(() => {
            renewalInProgress = null;
        });

    return renewalInProgress;
};

const start = (token, nextHandlers) => {
    clearTimers();
    currentToken = token || '';
    handlers = nextHandlers || {};
    scheduleRenewal();
    scheduleExpiryLogout();
};

const stop = () => {
    clearTimers();
    currentToken = '';
    handlers = {};
};

export default { start, stop };
